import { z } from 'zod';
import { narrativeBlockSchema } from './NarrativeBlock';
import { Comment } from '../comments';
import { Sprint } from '../ad-builder/Sprint';
import { SprintPacketApproval } from './SprintPacketApproval';

export const sprintPacketStatusSchema = z.enum([
  'draft',
  'ready',
  'presented',
  'archived',
]);

export type SprintPacketStatus = z.infer<typeof sprintPacketStatusSchema>;

const isoDateTimeStringSchema = z
  .string()
  .refine(
    (value) =>
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(
        value,
      ) && !Number.isNaN(new Date(value).getTime()),
    'Invalid ISO datetime string',
  );

export const sentimentScaleSchema = z.enum(['yes', 'maybe', 'no']).nullable();
export type SentimentScale = z.infer<typeof sentimentScaleSchema>;

export const competitorsPositionSchema = z
  .enum(['below', 'equal', 'above'])
  .nullable();
export type CompetitorsPosition = z.infer<typeof competitorsPositionSchema>;

export const clientSentimentSchema = z.object({
  likedToday: z.string().nullable(),
  competitorsPosition: competitorsPositionSchema,
  competitorsNotes: z.string().nullable(),
  wouldRenew: sentimentScaleSchema,
  wouldRenewNotes: z.string().nullable(),
  wouldRecommend: sentimentScaleSchema,
  wouldRecommendNotes: z.string().nullable(),
  updatedAt: z.string(),
});

export type ClientSentiment = z.infer<typeof clientSentimentSchema>;

export const sprintPacketSchema = z.object({
  id: z.number(),
  businessID: z.number(),
  sprintID: z.number(),
  status: sprintPacketStatusSchema,
  narrative: z.array(narrativeBlockSchema).default([]),
  clientSentiment: clientSentimentSchema.nullish(),
  createdByUserID: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  presentedAt: isoDateTimeStringSchema.nullish(),
});

export type SprintPacket = z.infer<typeof sprintPacketSchema>;

export const CreateSprintPacketDTO = sprintPacketSchema.pick({
  sprintID: true,
});

export type CreateSprintPacket = z.infer<typeof CreateSprintPacketDTO>;

// Per-block patch: server merges by sectionKey using updatedAt as a
// tiebreaker, so two sections autosaving in parallel can't clobber each other.
// The full `narrative` array is still accepted for bulk operations (initial
// load, admin tooling), but interactive editors should send `narrativeBlock`.
export const narrativeBlockPatchSchema = narrativeBlockSchema.extend({
  updatedAt: z.string(),
});
export type NarrativeBlockPatch = z.infer<typeof narrativeBlockPatchSchema>;

// Shallow per-field merge for clientSentiment. Each entry carries its own
// updatedAt; the server keeps the newer value field-by-field, so out-of-order
// saves of different fields can't clobber each other.
export const clientSentimentPatchSchema = clientSentimentSchema
  .partial()
  .extend({
    updatedAt: z.string(),
  });
export type ClientSentimentPatch = z.infer<typeof clientSentimentPatchSchema>;

export const UpdateSprintPacketDTO = sprintPacketSchema
  .pick({
    status: true,
    presentedAt: true,
    narrative: true,
    clientSentiment: true,
  })
  .partial()
  .extend({
    narrativeBlock: narrativeBlockPatchSchema.optional(),
    clientSentimentPatch: clientSentimentPatchSchema.optional(),
  });

export type UpdateSprintPacket = z.infer<typeof UpdateSprintPacketDTO>;

export interface SprintPacketPerformance {
  spend?: number;
  cpa?: number;
  conversions?: number;
  budget?: number;
  budgetTarget?: number;
}

export interface SprintPacketHydrated extends SprintPacket {
  sprint: Sprint;
  performance: SprintPacketPerformance | null;
  approvals: SprintPacketApproval[];
  comments: Comment[];
}
